import { FlowSessionWorkItem, Message, ToolCall } from "@cohesive/api-client";
import { dedupeToolCallsByIndex } from "../ToolCall/dedupeToolCallsByIndex";
import {
  AskQuestion,
  isAskUserQuestion,
  parseAskAnswerMessage,
  parseAskUserQuestion,
} from "./harnessAskUserQuestion";

export type HarnessToolCallOutcome = "running" | "ok" | "error" | "denied";

export interface HarnessToolCallItem {
  /** `message.id:call.index` — invariant across the delta → terminal event →
   *  refetch lifecycle (the call's own `id` is not), so safe as a React key. */
  key: string;
  message: Message;
  call: ToolCall;
  outcome: HarnessToolCallOutcome;
}

/** One entry of a tool run, in order: a call, or a thought the model had
 *  between two calls of the same tool (folded in rather than splitting the
 *  run). */
export type HarnessToolRunEntry =
  | { kind: "call"; item: HarnessToolCallItem }
  | { kind: "thought"; message: Message };

export type HarnessAgentStatus =
  | "queued"
  | "running"
  | "done"
  | "failed"
  | "stopped";

/** One sub-agent on the board: its launch call, the work item agentd reports
 *  for it (joined on `call_id`), and — when the API attributes rows to it —
 *  its own outline. */
export interface HarnessAgentEntry {
  key: string;
  callId: string;
  launch: HarnessToolCallItem;
  name: string;
  subagentType?: string;
  prompt?: string;
  status: HarnessAgentStatus;
  workItem?: FlowSessionWorkItem;
  toolUses?: number;
  lastTool?: string;
  summary?: string;
  durationMs?: number;
  error?: string;
  outline?: HarnessOutline;
}

/**
 * One row of the harness activity rail, in render order.
 * - `thoughts`: adjacent thinking bursts merged into one item.
 * - `tool-run`: consecutive calls to one tool, possibly spanning messages,
 *   with any thoughts between them folded in.
 * - `agents`: consecutive Agent/Task launches as one board.
 * - `confirmation`: a call parked on a permission ask.
 */
export type HarnessRailItem =
  | { kind: "thoughts"; key: string; messages: Message[] }
  | { kind: "confirmation"; key: string; item: HarnessToolCallItem }
  | {
      /** A completed/superseded AskUserQuestion — the active one is lifted out
       *  of the rail into the composer panel (see findActiveAsk); this is its
       *  compact record in the transcript. `answers` maps question header →
       *  chosen value, recovered from the answer user message when present. */
      kind: "ask";
      key: string;
      item: HarnessToolCallItem;
      questions: AskQuestion[];
      answers?: Record<string, string>;
    }
  | {
      kind: "tool-run";
      key: string;
      toolName: string;
      entries: HarnessToolRunEntry[];
      calls: HarnessToolCallItem[];
    }
  | { kind: "agents"; key: string; agents: HarnessAgentEntry[] };

/**
 * A chapter of the turn: the narration line the agent wrote (its header) and
 * everything it did until the next narration line. The first chapter has no
 * header when the turn opened with activity rather than words.
 */
export interface HarnessSegment {
  key: string;
  header?: Message;
  items: HarnessRailItem[];
  toolCalls: number;
  agents: number;
  running: boolean;
  startMs?: number;
  endMs?: number;
}

export interface HarnessOutline {
  segments: HarnessSegment[];
  toolCalls: number;
  agents: number;
}

export interface HarnessOutlineContext {
  /** The text promoted to the answer bubble; skipped as a segment header. */
  answerMessageId?: string;
  workItems?: FlowSessionWorkItem[];
  /** AskUserQuestion answers by tool-call id (parsed from the answer user
   *  message), for rendering a completed ask step's chosen values. */
  askAnswers?: Record<string, Record<string, string>>;
  /** The tool-call id of the AskUserQuestion currently shown in the composer
   *  panel — skipped in the rail so it is not duplicated. */
  activeAskCallId?: string;
}

export function toolCallOutcome(call: ToolCall): HarnessToolCallOutcome {
  if (call.user_confirmation_status === "rejected") return "denied";
  if (call.result?.error || call.function.result?.error) return "error";
  if (
    call.status === "processing" ||
    call.user_confirmation_status === "confirmed" ||
    call.user_confirmation_status === "confirmed-processing"
  ) {
    return "running";
  }
  return "ok";
}

/** Whether a call launches a sub-agent (Claude Code `Agent`/`Task`, OpenCode
 *  `task`). */
export function isAgentLaunch(toolName: string): boolean {
  return /^(agent|task)$/i.test(toolName);
}

/**
 * Build the outline of a turn from its assistant rows. Pure and deterministic
 * — the same rows give the same keys — so a streaming turn re-derived on every
 * websocket burst and the same transcript loaded from the API produce the same
 * structure, and a new call or thought extends an existing item under its
 * existing key instead of adding a row.
 *
 * Rows that carry `harness_parent_tool_call_id` are sidechain: they belong to
 * the agent that call launched and nest under its board entry (one outline per
 * agent, built with the same rules), never in the mainline. Rows without it
 * are mainline — which today is every row, since the API does not expose the
 * field yet.
 */
export function buildHarnessOutline(
  assistantMessages: Message[],
  ctx: HarnessOutlineContext = {},
): HarnessOutline {
  const mainline: Message[] = [];
  const byParent = new Map<string, Message[]>();
  for (const m of assistantMessages) {
    const parent = m.harness_parent_tool_call_id;
    if (!parent) {
      mainline.push(m);
      continue;
    }
    const rows = byParent.get(parent);
    if (rows) rows.push(m);
    else byParent.set(parent, [m]);
  }
  return outlineOf(mainline, ctx, byParent, 0);
}

// Sidechains nest at most this deep: an agent's own launches show as a board
// inside its outline, but their sidechains are not expanded further.
const MAX_SIDECHAIN_DEPTH = 2;

function outlineOf(
  messages: Message[],
  ctx: HarnessOutlineContext,
  byParent: Map<string, Message[]>,
  depth: number,
): HarnessOutline {
  const segments: HarnessSegment[] = [];
  let pendingThoughts: Message[] = [];

  const current = (): HarnessSegment => {
    let seg = segments[segments.length - 1];
    if (!seg) {
      seg = newSegment(`seg:${messages[0]?.id ?? "empty"}`, undefined);
      segments.push(seg);
    }
    return seg;
  };
  const lastItem = (): HarnessRailItem | undefined => {
    const seg = segments[segments.length - 1];
    return seg?.items[seg.items.length - 1];
  };
  const push = (item: HarnessRailItem, message: Message) => {
    const seg = current();
    seg.items.push(item);
    noteTime(seg, message);
  };
  const flushThoughts = () => {
    if (!pendingThoughts.length) return;
    const last = lastItem();
    if (last?.kind === "thoughts") {
      last.messages.push(...pendingThoughts);
    } else {
      push(
        {
          kind: "thoughts",
          key: `${pendingThoughts[0].id}:thinking`,
          messages: [...pendingThoughts],
        },
        pendingThoughts[pendingThoughts.length - 1],
      );
    }
    pendingThoughts = [];
  };

  for (const message of messages) {
    if (message.thinking_content) pendingThoughts.push(message);

    if (message.content && message.id !== ctx.answerMessageId) {
      flushThoughts();
      segments.push(newSegment(`seg:${message.id}`, message));
    }

    for (const call of dedupeToolCallsByIndex(message.tool_calls) ?? []) {
      const item: HarnessToolCallItem = {
        key: `${message.id}:${call.index}`,
        message,
        call,
        outcome: toolCallOutcome(call),
      };

      // AskUserQuestion is lifted out of the rail: while pending-and-active it
      // becomes the composer panel (findActiveAsk / HarnessChatArea), and once
      // answered it renders as a compact completed step here. A malformed
      // payload falls through to the generic confirmation card below.
      if (isAskUserQuestion(call)) {
        const questions = parseAskUserQuestion(call);
        if (questions) {
          // The active ask lives in the composer panel — leave it out of the
          // rail so it is not shown twice.
          if (call.id === ctx.activeAskCallId) break;
          flushThoughts();
          push(
            {
              kind: "ask",
              key: item.key,
              item,
              questions,
              answers: ctx.askAnswers?.[call.id],
            },
            message,
          );
          if (call.user_confirmation_status === "pending") break;
          continue;
        }
      }

      // A call parked on a permission ask is the user's move: it stands alone
      // with its confirmation card, and the calls queued behind it in the same
      // message stay hidden until it resolves (as the legacy rail does).
      if (call.user_confirmation_status === "pending") {
        flushThoughts();
        push({ kind: "confirmation", key: item.key, item }, message);
        break;
      }

      const toolName = call.function.name;
      if (isAgentLaunch(toolName)) {
        flushThoughts();
        const entry = agentEntry(item, ctx, byParent, depth);
        const last = lastItem();
        if (last?.kind === "agents") {
          last.agents.push(entry);
          noteTime(current(), message);
        } else {
          push({ kind: "agents", key: item.key, agents: [entry] }, message);
        }
        continue;
      }

      const last = lastItem();
      if (last?.kind === "tool-run" && last.toolName === toolName) {
        // Thoughts between two calls of the same tool ride inside the run.
        for (const thought of pendingThoughts) {
          last.entries.push({ kind: "thought", message: thought });
        }
        pendingThoughts = [];
        last.entries.push({ kind: "call", item });
        last.calls.push(item);
        noteTime(current(), message);
      } else {
        flushThoughts();
        push(
          {
            kind: "tool-run",
            key: item.key,
            toolName,
            entries: [{ kind: "call", item }],
            calls: [item],
          },
          message,
        );
      }
    }
  }
  // A trailing thought is the live "now" line while the model is still
  // thinking, and a plain Thoughts row once it has moved on.
  flushThoughts();

  let toolCalls = 0;
  let agents = 0;
  for (const seg of segments) {
    for (const item of seg.items) {
      if (item.kind === "tool-run") {
        seg.toolCalls += item.calls.length;
        if (item.calls.some((c) => c.outcome === "running")) seg.running = true;
      } else if (item.kind === "confirmation") {
        seg.toolCalls += 1;
        seg.running = true;
      } else if (item.kind === "agents") {
        seg.agents += item.agents.length;
        if (
          item.agents.some(
            (a) => a.status === "running" || a.status === "queued",
          )
        ) {
          seg.running = true;
        }
      }
    }
    toolCalls += seg.toolCalls;
    agents += seg.agents;
  }
  return { segments, toolCalls, agents };
}

function newSegment(key: string, header: Message | undefined): HarnessSegment {
  const seg: HarnessSegment = {
    key,
    header,
    items: [],
    toolCalls: 0,
    agents: 0,
    running: false,
  };
  if (header) noteTime(seg, header);
  return seg;
}

function noteTime(seg: HarnessSegment, message: Message) {
  for (const ts of [message.created_at, message.updated_at, message.sent_at]) {
    const t = ts ? Date.parse(ts) : NaN;
    if (Number.isNaN(t)) continue;
    seg.startMs = seg.startMs == null ? t : Math.min(seg.startMs, t);
    seg.endMs = seg.endMs == null ? t : Math.max(seg.endMs, t);
  }
}

function agentEntry(
  launch: HarnessToolCallItem,
  ctx: HarnessOutlineContext,
  byParent: Map<string, Message[]>,
  depth: number,
): HarnessAgentEntry {
  const callId = launch.call.id;
  const args = argsOf(launch.call);
  const workItem = ctx.workItems?.find(
    (w) => w.kind === "subagent" && w.call_id === callId,
  );
  const sidechain =
    depth < MAX_SIDECHAIN_DEPTH ? byParent.get(callId) : undefined;
  return {
    key: launch.key,
    callId,
    launch,
    name:
      workItem?.name ||
      str(args.description) ||
      workItem?.description ||
      "Agent",
    subagentType: workItem?.subagent_type || str(args.subagent_type),
    prompt: str(args.prompt),
    status: agentStatus(launch, workItem),
    workItem,
    toolUses: workItem?.tool_uses,
    lastTool: workItem?.last_tool,
    summary: workItem?.summary,
    durationMs: workItem?.duration_ms,
    error: workItem?.error,
    outline: sidechain?.length
      ? outlineOf(sidechain, { workItems: ctx.workItems }, byParent, depth + 1)
      : undefined,
  };
}

function agentStatus(
  launch: HarnessToolCallItem,
  workItem: FlowSessionWorkItem | undefined,
): HarnessAgentStatus {
  if (workItem) {
    switch (workItem.phase) {
      case "running":
        return "running";
      case "completed":
        return "done";
      case "failed":
      case "lost":
      case "killed_at_ceiling":
        return "failed";
      default:
        return "stopped";
    }
  }
  // No work item yet: the launch call itself is the only signal. While it is
  // in flight the agent is queued; once the harness acknowledged the launch it
  // is running until agentd reports otherwise.
  if (launch.outcome === "error" || launch.outcome === "denied")
    return "failed";
  return launch.outcome === "running" ? "queued" : "running";
}

function argsOf(call: ToolCall): Record<string, unknown> {
  let args: unknown = call.function.arguments;
  if (typeof args === "string") {
    try {
      args = JSON.parse(args);
    } catch {
      return {};
    }
  }
  return args && typeof args === "object" && !Array.isArray(args)
    ? (args as Record<string, unknown>)
    : {};
}

function str(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

// Argument keys that name what a call acted on, most specific first. Cohesive
// tools carry `action_description`; the harness tools (Claude Code, Codex,
// OpenCode) carry paths, commands, patterns and queries under these names.
const SUMMARY_KEYS = [
  "action_description",
  "description",
  "file_path",
  "path",
  "notebook_path",
  "command",
  "cmd",
  "pattern",
  "query",
  "url",
  "prompt",
  "skill",
  "name",
];

const SUMMARY_MAX_CHARS = 160;

/**
 * One-line gist of a call from its arguments — the path it read, the command
 * it ran — or undefined when nothing short describes it.
 */
export function summarizeToolCall(call: ToolCall): string | undefined {
  const record = argsOf(call);
  const pick = (value: unknown): string | undefined => {
    if (typeof value !== "string") return undefined;
    const line = value.replace(/\s+/g, " ").trim();
    if (!line) return undefined;
    return line.length > SUMMARY_MAX_CHARS
      ? `${line.slice(0, SUMMARY_MAX_CHARS - 1)}…`
      : line;
  };
  for (const key of SUMMARY_KEYS) {
    const summary = pick(record[key]);
    if (summary) return summary;
  }
  for (const value of Object.values(record)) {
    const summary = pick(value);
    if (summary) return summary;
  }
  return undefined;
}

/** First line of a thought, for the collapsed / live single-line rendering. */
export function firstThoughtLine(text: string): string {
  const line = text
    .split(/\n+/)
    .map((l) => l.replace(/^[#>*\-\s]+/, "").trim())
    .find(Boolean);
  return line ?? "";
}

/** Last line of a thought — what the model is thinking right now. */
export function latestThoughtLine(text: string): string {
  const lines = text
    .split(/\n+/)
    .map((l) => l.replace(/^[#>*\-\s]+/, "").trim())
    .filter(Boolean);
  return lines[lines.length - 1] ?? "";
}

// ── AskUserQuestion helpers (cross-turn) ─────────────────────────────────────

export interface HarnessActiveAsk {
  message: Message;
  call: ToolCall;
  questions: AskQuestion[];
}

/**
 * The AskUserQuestion the user is currently expected to answer, if any: a
 * pending, parseable AskUserQuestion call in the last turn with no user message
 * after it. Once the user answers (a new user-message turn) it is no longer the
 * last turn's trailing ask, so the panel dismisses and it renders as a
 * completed step instead. Malformed asks return null so the generic
 * confirmation card handles them.
 */
export function findActiveAsk(messages: Message[]): HarnessActiveAsk | null {
  // Walk back to the last user message: the current turn is everything after
  // it. A pending ask there is the live one.
  let start = 0;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].origin === "user") {
      start = i;
      break;
    }
  }
  for (let i = messages.length - 1; i >= start; i--) {
    const m = messages[i];
    if (m.origin === "user" && i !== start) return null;
    for (const call of dedupeToolCallsByIndex(m.tool_calls) ?? []) {
      if (
        isAskUserQuestion(call) &&
        call.user_confirmation_status === "pending"
      ) {
        const questions = parseAskUserQuestion(call);
        if (questions) return { message: m, call, questions };
      }
    }
  }
  return null;
}

/**
 * Map each answered AskUserQuestion call id to the header→value answers, parsed
 * from the user message that answered it (the next user message after the ask).
 * Lets a completed ask step show the chosen values after reload, when only the
 * transcript remains.
 */
export function buildAskAnswers(
  messages: Message[],
): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {};
  for (let i = 0; i < messages.length; i++) {
    for (const call of dedupeToolCallsByIndex(messages[i].tool_calls) ?? []) {
      if (!isAskUserQuestion(call)) continue;
      for (let j = i + 1; j < messages.length; j++) {
        if (messages[j].origin === "user" && messages[j].content) {
          out[call.id] = parseAskAnswerMessage(messages[j].content);
          break;
        }
      }
    }
  }
  return out;
}
